import React from 'react';
import { Mail, MessageSquare, X } from 'lucide-react';

/**
 * Invoice delivery status row — shown on the Owings page above the
 * Take Payment / Mark as Paid / Request Payment action buttons.
 *
 * Renders three pill badges (SMS, WhatsApp, Email) with iconography that
 * mirrors the WhatsApp UX language so it's instantly recognisable:
 *
 *   Sent      -> single grey tick
 *   Delivered -> single dark tick
 *   Read      -> double blue tick (WhatsApp only)
 *   Opened    -> double tick (Email only)
 *   Failed    -> red X
 *   Not sent  -> dim X
 *
 * The state shape matches what /api/owings returns:
 *   {
 *     sms:      { state, ts },
 *     whatsapp: { state, ts },
 *     email:    { state, ts },
 *     invoice_id
 *   }
 */
const TICK_GREY = '#94a3b8';
const TICK_DARK = '#475569';
const TICK_BLUE = '#3b82f6';
const RED = '#ef4444';

// SVG icons local to the component — kept tiny so the pills sit in one row.
const SingleTick = ({ color = TICK_GREY }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 8.5L6.5 12L13.5 4"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DoubleTick = ({ color = TICK_BLUE }) => (
  <svg width="16" height="12" viewBox="0 0 20 16" fill="none" aria-hidden="true">
    <path
      d="M2 8.5L5.5 12L11 4.5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 8.5L11.5 12L18 4.5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Map a state to its visual: icon + accessible label
 */
const stateVisual = (channel, state) => {
  if (state === 'read') return { icon: <DoubleTick color={TICK_BLUE} />, label: 'Read' };
  if (state === 'opened') return { icon: <DoubleTick color={TICK_DARK} />, label: 'Opened' };
  if (state === 'delivered') return { icon: <SingleTick color={TICK_DARK} />, label: 'Delivered' };
  if (state === 'sent') return { icon: <SingleTick color={TICK_GREY} />, label: 'Sent' };
  if (state === 'failed') return {
    icon: <X size={12} color={RED} strokeWidth={3} />,
    label: 'Failed',
  };
  return { icon: <X size={12} color="#cbd5e1" strokeWidth={2.5} />, label: 'Not sent' };
};

const Pill = ({ icon: Icon, label, channel, state, testId }) => {
  const visual = stateVisual(channel, state);
  const stateLabel = visual.label;
  const dimmed = !state;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${
        dimmed
          ? 'bg-zinc-50 border-zinc-200 text-zinc-400'
          : 'bg-white border-zinc-200 text-zinc-600'
      }`}
      title={`${label}: ${stateLabel}`}
      data-testid={testId}
      data-state={state || 'none'}
    >
      <Icon size={12} className="opacity-70" />
      <span>{label}</span>
      <span className="flex items-center" aria-label={stateLabel}>
        {visual.icon}
      </span>
    </span>
  );
};

const InvoiceDeliveryRow = ({ status }) => {
  // status missing → no invoice yet (probably owed from a balance carried in
  // from outside the system). Render the channels dim so the user knows the
  // info simply isn't available, but keep the layout consistent.
  const sms = status?.sms?.state || null;
  const whatsapp = status?.whatsapp?.state || null;
  const email = status?.email?.state || null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 mt-3"
      data-testid="invoice-delivery-row"
    >
      <span className="text-[11px] uppercase tracking-wide font-semibold text-zinc-400 mr-1">
        Invoice
      </span>
      <Pill
        icon={MessageSquare}
        label="SMS"
        channel="sms"
        state={sms}
        testId="delivery-badge-sms"
      />
      <Pill
        icon={(props) => (
          <svg viewBox="0 0 24 24" width={props.size || 12} height={props.size || 12} fill="currentColor" aria-hidden="true">
            <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
          </svg>
        )}
        label="WhatsApp"
        channel="whatsapp"
        state={whatsapp}
        testId="delivery-badge-whatsapp"
      />
      <Pill
        icon={Mail}
        label="Email"
        channel="email"
        state={email}
        testId="delivery-badge-email"
      />
    </div>
  );
};

export default InvoiceDeliveryRow;
